// WorkAbroadPricing.js
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle } from 'lucide-react';

const WorkAbroadPricing = () => {
  const coverageItems = [
    'Eligibility screening and role matching',
    'Placement into the work abroad pipeline',
    'Interview coordination with partner employers',
    'Interview preparation and readiness support',
    'Documentation and compliance guidance',
    'Ongoing support throughout the hiring process'
  ];

  const importantNotes = [
    'Fees are for support services only',
    'Fees are not tied to salary or employment outcomes',
    'Employment contracts are between candidates and employers',
    'Notapaths does not collect salaries or employer fees'
  ];

  return (
    <section className="py-16 bg-[#F5F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
              Work Abroad Pricing Structure
            </h2>
          </motion.div>

          {/* Pricing Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white p-8 rounded-lg shadow-sm mb-6"
          >
            <h3 className="text-xl font-semibold text-[#202356] mb-4">
              Work Abroad Support
            </h3>
            <p className="text-gray-600 text-base font-light leading-relaxed mb-6">
              Work Abroad service fees apply only to candidates who qualify and proceed into the Work Abroad pipeline.
            </p>

            {/* Coverage List */}
            <div className="mb-6">
              <p className="text-gray-700 text-base font-normal mb-4">
                Our Work Abroad fees typically cover:
              </p>
              <div className="space-y-3">
                {coverageItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 + (index * 0.1) }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 size={18} className="text-gray-700 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-700 text-base font-light">
                      {item}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Fee Structure */}
            <div className="bg-[#F5F8FA] p-6 rounded-lg mb-4 border border-gray-200">
              <p className="text-sm font-semibold text-[#202356] mb-4">
                Interview Slot Pricing
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-[#202356]/5 to-transparent rounded-lg">
                  <div>
                    <p className="text-lg font-semibold text-[#202356]">Per Interview Slot</p>
                    <p className="text-sm text-gray-600">Complete interview preparation and coordination</p>
                  </div>
                  <p className="text-2xl font-bold text-[#202356]">$199</p>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Note:</span> This fee covers comprehensive interview support including preparation, coordination with employers, and ongoing guidance throughout the hiring process.
                </p>
              </div>
            </div>

            <p className="text-gray-600 text-sm font-light leading-relaxed">
              Fees are explained clearly before pipeline placement.
            </p>
          </motion.div>

          {/* Important Notes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="bg-blue-50 border border-blue-200 rounded-lg p-6"
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-base font-semibold text-[#202356]">
                Important notes:
              </p>
            </div>
            <div className="space-y-2 ml-8">
              {importantNotes.map((note, index) => (
                <p key={index} className="text-gray-700 text-sm font-light leading-relaxed">
                  • {note}
                </p>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WorkAbroadPricing;
